<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { useI18n } from "vue-i18n"
import { ref, computed, reactive, watch, toRaw, onMounted, onBeforeUnmount, nextTick } from "vue"
import { showSnackbar } from '@/helper'
import { useAuth } from "@/app/Checkout/composables/useAuth"
import { useUser } from "@/app/Checkout/composables/useUser"
import { useGender } from "@/app/Checkout/composables/useGender"
import { useAddresses } from "@/app/Checkout/composables/useAddresses"
import NBToggle from "@/app/Checkout/components/common/NBToggle/index.vue"
import NBFormInput from "@/app/Checkout/components/common/NBFormInput/index.vue"
import NBPhoneInput from "@/app/Checkout/components/common/NBPhoneInput/index.vue"
import NBFormSelect from "@/app/Checkout/components/common/NBFormSelect/index.vue"
import NBRadioGroup from "@/app/Checkout/components/common/NBRadioGroup/index.vue"
import { useAddressValidation } from "@/app/Checkout/composables/useAddressValidation"

const props = defineProps({
  modelValue: Object,
  allowStation: {
    type: Boolean,
    default: true,
  },
  hideDefault: {
    type: Boolean,
    default: false,
  },
  showSave: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: {},
  },
})

const { t } = useI18n()
const { isLoggedIn } = useAuth()
const { genderOptions } = useGender()
const { countryPhoneCodes } = useUser()
const { countries, requiredFields } = useAddresses()
const { validate, clearField, emptyAddress } = useAddressValidation()
const addressTypes = ref([
  {
    type: 'address',
    label: t('address.title'),
  },
  {
    type: 'station',
    label: t('address.dhl_station'),
  },
])
const localAddress = reactive(emptyAddress())
const initialAddress = reactive(emptyAddress())
const baselineKey = ref(null)
const formElement = ref(null)
const wasInteractingWithForm = ref(false)
const isPointerDownInsideForm = ref(false)
const isGermany = computed(() => localAddress.country === "DE")
const defaultRequiredFields = computed(() => {
  const base = ["gender", "firstname", "lastname", "zip", "city"]

  if (!isLoggedIn.value) base.push("email")

  if (localAddress.station_deliver !== "address") {
    base.push("packstation", "postnumber")
  } else {
    base.push("address", "house_number", "country")
  }

  return base
})

const effectiveRequiredFields = computed(() => {
  const cartRequired = requiredFields.value || []

  const stationFields = ["packstation", "postnumber"]
  const addressFields = ["address", "house_number", "country"]

  // If the backend provides required_fields, use it as a base,
  // but treat station vs address fields as mutually exclusive per tab.
  const base = cartRequired.length ? cartRequired : defaultRequiredFields.value
  const filteredBase =
    localAddress.station_deliver !== "address"
      ? base.filter((f) => !addressFields.includes(f))
      : base.filter((f) => !stationFields.includes(f))

  const requiredSet = new Set(filteredBase)

  if (!cartRequired.length && !isLoggedIn.value) {
    requiredSet.add("email")
  }

  if (localAddress.station_deliver !== "address") {
    stationFields.forEach((f) => requiredSet.add(f))
  } else {
    addressFields.forEach((f) => requiredSet.add(f))
  }

  return Array.from(requiredSet)
})

const isComplete = computed(() => {
  const requiredList = effectiveRequiredFields.value
  return requiredList.every((field) => {
    const value = localAddress?.[field]
    if (typeof value === "string") return value.trim().length > 0
    return value !== null && value !== undefined && value !== ""
  })
})

const canSubmit = computed(() => isDirty.value && isComplete.value)
const isDirty = computed(() => {
  const current = normalize(pickDirtyComparableFields(localAddress))
  const initial = normalize(pickDirtyComparableFields(initialAddress))

  return JSON.stringify(current) !== JSON.stringify(initial)
})

const pickDirtyComparableFields = (address) => {
  const base = {
    gender: address?.gender,
    firstname: address?.firstname,
    lastname: address?.lastname,
    email: address?.email,
    company: address?.company,
    address_details: address?.address_details,
    zip: address?.zip,
    city: address?.city,
    phone: address?.phone,
    phone_country_code: address?.phone_country_code,
    is_default: address?.is_default,
    station_deliver: address?.station_deliver,
  }

  if (localAddress.station_deliver !== "address") {
    return {
      ...base,
      packstation: address?.packstation,
      postnumber: address?.postnumber,
    }
  }

  return {
    ...base,
    address: address?.address,
    house_number: address?.house_number,
    country: address?.country,
  }
}

const normalize = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      value === null || value === undefined ? "" : value
    ])
  )
}

// Create a computed proxy for gender
const genderValue = computed({
  get() {
    return localAddress.gender
  },
  set(selectedOption) {
    localAddress.gender = selectedOption?.value || selectedOption
  },
})

// Create a computed proxy for country
const countryValue = computed({
  get() {
    return localAddress.country
  },
  async set(selectedOption) {
    const newCountry = selectedOption?.value || selectedOption
    const oldCountry = localAddress.country

    localAddress.country = newCountry

    if (newCountry !== oldCountry) {
      emit('country-change', newCountry)
    }
  }
})

const emit = defineEmits([
  'update:modelValue',
  'update:isDirty',
  'update:isComplete',
  'country-change',
  'clearField',
  'valid',
  'leave',
])

watch(
  () => props.modelValue,
  (val) => {
    if (!val) return

    Object.assign(localAddress, emptyAddress(), val)

    // Keep a stable "baseline" for dirty checks.
    // Only reset it when switching to a different address (id change) or first init.
    const nextKey = val?.id ?? '__new__'
    if (baselineKey.value !== nextKey) {
      baselineKey.value = nextKey
      Object.assign(initialAddress, emptyAddress(), val)
    }
  },
  { deep: true, immediate: true }
)

watch(isDirty, (value) => {
  emit('update:isDirty', value)
})

watch(isComplete, (value) => {
  emit('update:isComplete', value)
}, { immediate: true })

function markFormInteraction() {
  wasInteractingWithForm.value = true
}

async function handleDocumentPointerDown(event) {
  if (!formElement.value) {
    return
  }

  const clickedInsideThisForm = formElement.value.contains(event.target)

  if (clickedInsideThisForm) {
    wasInteractingWithForm.value = true
    return
  }

  if (!wasInteractingWithForm.value || !isDirty.value) {
    wasInteractingWithForm.value = false
    return
  }

  if (clickedInsideThisForm) {
    wasInteractingWithForm.value = true
    isPointerDownInsideForm.value = true

    window.setTimeout(() => {
      isPointerDownInsideForm.value = false
    }, 100)

    return
  }

  const target = event.target

  event.stopPropagation()

  await nextTick()

  emit('leave', {
    address: { ...toRaw(localAddress) },
    target,
  })

  wasInteractingWithForm.value = false
}

onMounted(async () => {
  document.addEventListener('pointerdown', handleDocumentPointerDown, true)

  await nextTick()
  const address = localAddress || {}
  const isFilled = Boolean(address.gender || address.firstname || address.lastname)
  if (isFilled && (address.is_invalid || !isComplete.value)) {
    handleSubmit()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
})

function handleSubmit() {
  emit('submit', { ...toRaw(localAddress) })
}

function validateForm(allowStation = true) {
  const isValid = validate(localAddress, allowStation);

  if (!isValid) {
    showSnackbar('error', 'iconfont-info', t('messages.page.revocation.error'))
    return false
  }

  return true
}

function formData() {
  return { ...toRaw(localAddress) }
}

defineExpose({ formData, validateForm })
</script>
